import type { ScheduledDetailedCopy } from './scheduled-page-copy-translations';

export const scheduledDetailedCopyChinese = {
  zh: {
    status: {
      upcoming: '即将运行',
      running: '运行中',
      waitingBackground: '等待后台任务',
      succeeded: '已成功',
      failed: '已失败',
      skipped: '已跳过',
      needsAttention: '需要处理',
      unknownOutcome: '结果未知',
      canceled: '已取消',
      malformed: '不可用',
      paused: '已暂停',
    },
    editor: {
      createTitle: '新建计划',
      editTitle: '编辑计划',
      description: '设置任务、时间、上下文和无人值守权限。',
      stepOf: '第 {step} 步，共 4 步',
      task: '任务',
      timing: '时间',
      execution: '执行',
      review: '检查',
      name: '计划名称',
      namePlaceholder: '例如：周一绩效简报',
      instructions: '代理指令',
      instructionsPlaceholder: '说明代理应生成的结果以及允许其操作的范围。',
      sourceChat: '来源聊天',
      scheduleThisChat: '为此聊天创建计划',
      sourceSnapshot: '保存时会创建私有快照。之后的聊天更改不会影响此计划。',
      sourceUnavailable: '来源聊天不可用，或你已无权访问。',
      sourceChatLockedNote:
        '重复使用来源聊天的计划必须保留持续上下文，并设为私有。',
      recurrence: '重复',
      once: '仅一次',
      hourly: '每小时',
      daily: '每天',
      weekdays: '工作日',
      selectedWeekdays: '选定的星期',
      weekly: '每周',
      monthly: '每月',
      date: '日期',
      time: '当地时间',
      dayOfMonth: '每月日期',
      whenPlaceholder: '明天',
      whenInvalid: '请输入日期，例如“明天”。',
      end: '结束条件',
      never: '永不结束',
      onDate: '在指定日期',
      afterOccurrences: '运行指定次数后',
      occurrences: '运行次数',
      nextRuns: '接下来的运行',
      schedulePreview: '计划预览',
      noEndDate: '无结束日期。',
      runsUntil: '运行至 {date}。',
      totalRuns: '共运行 {count} 次。',
      resolvingTime: '服务器正在核对此当地时间…',
      serverTimeRequired: '保存前，服务器必须确认此当地时间。',
      contextMode: '聊天上下文',
      persistent: '持续上下文',
      persistentDescription: '在所有运行中复用一个计划专属聊天。',
      isolated: '每次运行使用新聊天',
      isolatedDescription: '每次运行均从独立上下文开始。',
      visibility: '可见范围',
      private: '私有',
      privateDescription: '只有你可以查看完整指令、对话、工具活动和结果。',
      workspace: '工作区',
      workspaceWarning:
        '与工作区共享后，完整指令、对话、工具活动和结果都将公开。',
      creditPolicy: '工作区额度政策',
      creditsAvailable: '运行会使用工作区现有的额度余额和限制。',
      creditsUnavailable: '此工作区目前没有可用额度。计划可能会在运行时暂停。',
      fullAuthorityTitle: '代理操作',
      fullAuthorityDescription:
        '每次运行时，代理都会重新检查你当前的访问权限，并可能在不询问的情况下执行破坏性操作。',
      authorityAcknowledgment: '允许执行任何操作，无需先询问。',
      authorityRequired:
        '允许代理操作以创建此定时任务。运行过程无人值守，之后没有人能够批准。',
      recurrenceUnresolved: '无法确认此运行时间。请重新选择日期和时间。',
      notifications: '应用内通知',
      notificationsDescription: '对于需要你注意的最终结果，只会通知一次。',
      reviewSummary: '创建计划前，请仔细检查任务内容和运行频率。',
      required: '此项为必填项。',
      invalid: '请输入有效值。',
      past: '请选择将来的时间。',
      endBeforeStart: '请将结束时间设在首次运行之后。',
      draftRestored: '已恢复你未保存的草稿。',
      tooLong: '此值过长。',
      createDisabled: '你无权在此工作区中创建计划。',
      saved: '计划已保存',
      unsavedTitle: '要放弃未保存的更改吗？',
      unsavedDescription: '如果现在关闭，对此计划的更改将会丢失。',
      discardChanges: '放弃更改',
    },
    detail: {
      title: '计划详情',
      cadence: '运行频率',
      owner: '所有者',
      timezone: '时区',
      nextRun: '下次运行',
      lastRun: '上次运行',
      prompt: '完整指令',
      sourceContext: '已保存的来源上下文',
      executionMode: '执行模式',
      recentUsage: '近期用量',
      credits: '额度',
      tools: '操作',
      history: '运行记录',
      transcript: '对话',
      resultFiles: '结果文件',
      noResultFiles: '此次运行未记录结果文件。',
      noRuns: '还没有运行记录。',
      noTranscript: '此运行目前没有可用的对话记录。',
      toolActivity: '活动',
      toolInput: '任务详情',
      toolResult: '结果',
      noToolActivity: '此运行没有活动。',
      loadMore: '加载更多',
      preview: '预览',
      sourceScheduled: '按计划运行',
      sourceManual: '手动运行',
      sourceWatchdog: '恢复运行',
      roleUser: '用户',
      roleAssistant: '助手',
      roleTool: '工具',
      roleSystem: '系统',
      runNow: '立即运行',
      pause: '暂停',
      resume: '继续',
      share: '与工作区共享',
      unshare: '设为私有',
      cancelRun: '取消正在运行的任务',
      archive: '归档',
      deleteSchedule: '删除计划',
      openThread: '查看对话',
      confirmArchive: '要归档此计划吗？',
      confirmArchiveDescription:
        '未来的任务将被取消，但历史记录和审计记录会保留。',
      confirmDelete: '删除此计划？',
      confirmDeleteDescription:
        '此计划将被永久删除。过去的对话和已生成的内容会保留在工作区中。',
      confirmUnshareDescription:
        '工作区成员将立即失去对提示、运行记录、工具活动和结果的访问权限。',
      confirmCancel: '要取消正在运行的任务吗？',
      confirmCancelDescription: '任务将在下一个安全取消点停止。',
      actionCompleted: '计划已更新',
      permissionDenied: '你可以查看此计划，但只有所有者或计划管理员才能修改。',
      needsAttention: '继续无人值守运行前，需要先检查本次运行。',
      runDetails: '运行详情',
    },
    notifications: {
      completed: '计划运行已完成',
      failed: '计划运行失败',
      attention: '计划运行需要处理',
      missed: '错过了一次性计划运行',
      viewRun: '查看运行',
      empty: '暂无运行通知。',
      unread: '未读',
      read: '已读',
      caughtUp: '已查看全部通知。',
    },
    errors: {
      generic: '出现问题，请重试。',
      permission: '你无权执行此操作。',
      disabled: '此工作区尚未启用计划代理。',
      credits: '工作区没有足够的额度。',
      limit: '工作区已达到有效计划数量上限。',
      runPending: '此计划已有一个等待中或正在运行的任务。',
      invalidTime: '请选择有效的未来时间。',
      notFound: '此计划或运行已不可用。',
    },
  },
  'zh-TW': {
    status: {
      upcoming: '即將執行',
      running: '執行中',
      waitingBackground: '等待背景工作',
      succeeded: '已成功',
      failed: '已失敗',
      skipped: '已略過',
      needsAttention: '需要處理',
      unknownOutcome: '結果未知',
      canceled: '已取消',
      malformed: '無法使用',
      paused: '已暫停',
    },
    editor: {
      createTitle: '新增排程',
      editTitle: '編輯排程',
      description: '設定任務、時間、內容脈絡和無人值守權限。',
      stepOf: '第 {step} 步，共 4 步',
      task: '任務',
      timing: '時間',
      execution: '執行',
      review: '檢查',
      name: '排程名稱',
      namePlaceholder: '例如：週一成效摘要',
      instructions: '代理指示',
      instructionsPlaceholder: '說明代理應產生的結果，以及允許操作的範圍。',
      sourceChat: '來源對話',
      scheduleThisChat: '為此對話建立排程',
      sourceSnapshot: '儲存時會建立私人快照。之後的對話變更不會影響此排程。',
      sourceUnavailable: '來源對話無法使用，或你已無權存取。',
      sourceChatLockedNote:
        '重複使用來源對話的排程必須保留持續內容脈絡，並設為私人。',
      recurrence: '重複',
      once: '僅一次',
      hourly: '每小時',
      daily: '每天',
      weekdays: '工作日',
      selectedWeekdays: '選定的星期',
      weekly: '每週',
      monthly: '每月',
      date: '日期',
      time: '當地時間',
      dayOfMonth: '每月日期',
      whenPlaceholder: '明天',
      whenInvalid: '請輸入日期，例如「明天」。',
      end: '結束條件',
      never: '永不結束',
      onDate: '在指定日期',
      afterOccurrences: '執行指定次數後',
      occurrences: '執行次數',
      nextRuns: '接下來的執行',
      schedulePreview: '排程預覽',
      noEndDate: '無結束日期。',
      runsUntil: '執行至 {date}。',
      totalRuns: '共執行 {count} 次。',
      resolvingTime: '伺服器正在核對此當地時間…',
      serverTimeRequired: '儲存前，伺服器必須確認此當地時間。',
      contextMode: '對話內容脈絡',
      persistent: '持續內容脈絡',
      persistentDescription: '在所有執行中重複使用一個排程專屬對話。',
      isolated: '每次執行使用新對話',
      isolatedDescription: '每次執行均從獨立的內容脈絡開始。',
      visibility: '顯示範圍',
      private: '私人',
      privateDescription: '只有你可以查看完整指示、對話、工具活動和結果。',
      workspace: '工作區',
      workspaceWarning:
        '與工作區共享後，完整指示、對話、工具活動和結果都會公開。',
      creditPolicy: '工作區額度政策',
      creditsAvailable: '執行會使用工作區現有的額度餘額和限制。',
      creditsUnavailable: '此工作區目前沒有可用額度。排程可能會在執行時暫停。',
      fullAuthorityTitle: '代理操作',
      fullAuthorityDescription:
        '每次執行時，代理都會重新檢查你目前的存取權，並可能在不詢問的情況下執行破壞性操作。',
      authorityAcknowledgment: '允許執行任何操作，無需事先詢問。',
      authorityRequired:
        '允許代理動作以建立此排程。執行過程無人看管，之後沒有人能夠核准。',
      recurrenceUnresolved: '無法確認此執行時間。請重新選擇日期與時間。',
      notifications: '應用程式內通知',
      notificationsDescription: '對於需要你注意的最終結果，只會通知一次。',
      reviewSummary: '建立排程前，請仔細檢查任務內容和執行頻率。',
      required: '此欄位為必填。',
      invalid: '請輸入有效值。',
      past: '請選擇未來的時間。',
      endBeforeStart: '請將結束時間設在首次執行之後。',
      draftRestored: '已還原你未儲存的草稿。',
      tooLong: '此值過長。',
      createDisabled: '你無權在此工作區中建立排程。',
      saved: '排程已儲存',
      unsavedTitle: '要放棄尚未儲存的變更嗎？',
      unsavedDescription: '如果現在關閉，對此排程的變更將會遺失。',
      discardChanges: '放棄變更',
    },
    detail: {
      title: '排程詳細資料',
      cadence: '執行頻率',
      owner: '擁有者',
      timezone: '時區',
      nextRun: '下次執行',
      lastRun: '上次執行',
      prompt: '完整指示',
      sourceContext: '已儲存的來源內容脈絡',
      executionMode: '執行模式',
      recentUsage: '近期用量',
      credits: '額度',
      tools: '操作',
      history: '執行記錄',
      transcript: '對話',
      resultFiles: '結果檔案',
      noResultFiles: '此次執行未記錄結果檔案。',
      noRuns: '還沒有執行記錄。',
      noTranscript: '此執行目前沒有可用的對話記錄。',
      toolActivity: '活動',
      toolInput: '任務詳細資料',
      toolResult: '結果',
      noToolActivity: '此執行沒有活動。',
      loadMore: '載入更多',
      preview: '預覽',
      sourceScheduled: '依排程執行',
      sourceManual: '手動執行',
      sourceWatchdog: '復原執行',
      roleUser: '使用者',
      roleAssistant: '助理',
      roleTool: '工具',
      roleSystem: '系統',
      runNow: '立即執行',
      pause: '暫停',
      resume: '繼續',
      share: '與工作區共享',
      unshare: '設為私人',
      cancelRun: '取消正在執行的任務',
      archive: '封存',
      deleteSchedule: '刪除排程',
      openThread: '檢視對話',
      confirmArchive: '要封存此排程嗎？',
      confirmArchiveDescription:
        '未來的任務將被取消，但歷史記錄和稽核記錄會保留。',
      confirmDelete: '刪除此排程？',
      confirmDeleteDescription:
        '此排程將被永久刪除。過去的對話和已產生的內容會保留在工作區中。',
      confirmUnshareDescription:
        '工作區成員將立即失去對提示、執行記錄、工具活動和結果的存取權限。',
      confirmCancel: '要取消正在執行的任務嗎？',
      confirmCancelDescription: '任務將在下一個安全取消點停止。',
      actionCompleted: '排程已更新',
      permissionDenied: '你可以查看此排程，但只有擁有者或排程管理員才能修改。',
      needsAttention: '繼續無人值守執行前，需要先檢查本次執行。',
      runDetails: '執行詳細資料',
    },
    notifications: {
      completed: '排程執行已完成',
      failed: '排程執行失敗',
      attention: '排程執行需要處理',
      missed: '錯過了一次性排程執行',
      viewRun: '查看執行',
      empty: '尚無執行通知。',
      unread: '未讀',
      read: '已讀',
      caughtUp: '已查看全部通知。',
    },
    errors: {
      generic: '發生問題，請再試一次。',
      permission: '你無權執行此操作。',
      disabled: '此工作區尚未啟用排程代理。',
      credits: '工作區沒有足夠的額度。',
      limit: '工作區已達到有效排程數量上限。',
      runPending: '此排程已有一個等待中或正在執行的工作。',
      invalidTime: '請選擇有效的未來時間。',
      notFound: '此排程或執行已無法使用。',
    },
  },
} satisfies Record<'zh' | 'zh-TW', ScheduledDetailedCopy>;
